// eslint-disable-next-line no-restricted-imports
import { css } from "@emotion/react";
// eslint-disable-next-line no-restricted-imports
import styled from "@emotion/styled";

import type { IconProps } from "metabase/ui";
import { Icon } from "metabase/ui";
import { lighten } from "metabase/ui/colors";
import { color } from "metabase/ui/utils/colors";

interface TreeNodeRootProps {
  isSelected: boolean;
  depth: number;
}

export const TreeNodeRoot = styled.li<TreeNodeRootProps>`
  display: flex;
  align-items: center;
  color: ${(props) =>
    props.isSelected ? color("text-primary-inverse") : color("core-brand")};
  background-color: ${(props) =>
    props.isSelected ? color("core-brand") : "unset"};
  /* Otherwise a solid fill distinguished only by color (WCAG 1.4.1) — add
     a real ring so the shape doesn't depend on perceiving the fill's hue. */
  outline: ${(props) =>
    props.isSelected ? `2px solid ${color("text-primary-inverse")}` : "none"};
  outline-offset: -4px;
  padding-left: ${(props) => props.depth + 0.5}rem;
  padding-right: 0.5rem;
  cursor: pointer;
  font-weight: 700;

  &:hover {
    background-color: ${(props) =>
      props.isSelected ? color("core-brand") : lighten("core-brand", 0.6)};
  }

  /* The roving-tabindex row that currently has keyboard focus had no
     visible indicator at all when not also the selected row — confirmed
     live (permissions Groups list) via an automated per-page focus audit.
     A plain :focus-visible rule alone would be overridden by the
     isSelected outline rule above when both are true, so this one is kept
     last so it always wins once focused. */
  &:focus-visible {
    outline: 2px solid var(--mb-color-input-focus);
    outline-offset: -2px;
  }
`;

export const ExpandToggleButton = styled.button`
  cursor: pointer;
  padding: 0.5rem 0.25rem 0.5rem 0.25rem;
  display: block;
  color: inherit;
  visibility: ${(props) => (props.hidden ? "hidden" : "visible")};
`;

interface ExpandToggleIconProps extends IconProps {
  isExpanded: boolean;
}

export const ExpandToggleIcon = styled(
  ({ isExpanded, ...props }: ExpandToggleIconProps) => (
    <Icon
      {...props}
      name={props.name ?? "chevronright"}
      size={props.size ?? 12}
    />
  ),
)`
  transition: transform 200ms;

  ${(props) =>
    props.isExpanded &&
    css`
      transform: rotate(90deg);
    `}
`;

export const NameContainer = styled.div`
  word-break: break-word;
  padding: 0.5rem 0.5rem 0.5rem 0.25rem;
  flex: 1;
`;

export const IconContainer = styled.div<{ transparent?: boolean }>`
  display: flex;
  align-items: center;
  padding: 0.25rem;
  opacity: ${({ transparent = true }) => (transparent ? 0.5 : 1)};
`;
